// ScheduleMonthView — lightweight month-aware grid (Mon-Fri × calendar weeks).
// Iter 27: rows are now anchored to the actual calendar week (Monday-of-week)
// rather than naive date/7 buckets, so dates render in correct calendar order
// and Mon..Fri cells line up properly even when the month begins mid-week.
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= on month-view clicks.
import useShadowNavigate from "@/lib/useShadowNavigate";
import { gradeColor } from "./utils";
import { providerBlockColor } from "@/lib/providerBlockColors";
import { TREATMENT_META } from "@/components/schedule/UnavailableBadge";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

function gc(students) {
  // FP3-121 — Use the shared K–12 palette helper so ScheduleMonthView
  // resolves Grade 7 to the same tile as the Builder + Provider week
  // view, and unknown grades fall back cleanly (not to Grade 1).
  return gradeColor(students?.[0]?.grade);
}

function fmtTime(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
}

function startOfMondayWeek(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay(); // 0=Sun..6=Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  x.setDate(x.getDate() + diff);
  return x;
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function ScheduleMonthView({ monthDates, mySessions, conflictSet }) {
  const navigate = useShadowNavigate();

  const grid = useMemo(() => {
    const sessionsByDate = {};
    for (const s of mySessions) {
      sessionsByDate[s.date] = sessionsByDate[s.date] || [];
      sessionsByDate[s.date].push(s);
    }
    // Iter 33d Follow-up 14f — Detect "all-day" personal blocks
    // (start ≤ 00:30 AND end ≥ 23:00) so the cell can render them as a
    // top banner across the day's column rather than a tiny half-chip
    // in a list of timed sessions. This is the only way the user could
    // visually tell that an "Every day · All day · every 3rd week"
    // provider rule was actually applied to the right weeks.
    const isAllDay = (s) =>
      s.block_kind === "personal" &&
      typeof s.start_time === "string" &&
      typeof s.end_time === "string" &&
      s.start_time <= "00:30" &&
      s.end_time >= "23:00";
    const rowsByMonday = new Map();
    for (const dateStr of monthDates) {
      const d = new Date(dateStr + "T12:00:00");
      const wd = d.getDay();
      if (wd < 1 || wd > 5) continue;
      const weekday = wd - 1;
      const mondayISO = isoDate(startOfMondayWeek(d));
      let row = rowsByMonday.get(mondayISO);
      if (!row) {
        row = [null, null, null, null, null];
        rowsByMonday.set(mondayISO, row);
      }
      const dayList = (sessionsByDate[dateStr] || []).slice().sort((a, b) =>
        a.start_time.localeCompare(b.start_time)
      );
      const allDay = dayList.filter(isAllDay);
      const timed = dayList.filter((s) => !isAllDay(s));
      row[weekday] = {
        date: dateStr,
        sessions: timed,
        allDay,
      };
    }
    // Per-row cadence indicator: collect distinct week_cadence values
    // across all timed/all-day sessions in this row, and the dominant
    // weeks_of_month label.
    return Array.from(rowsByMonday.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([mondayISO, row]) => {
        let rowCadence = null;
        let rowWom = null;
        for (const cell of row) {
          if (!cell) continue;
          for (const s of [...cell.allDay, ...cell.sessions]) {
            if (typeof s.week_cadence === "number" && s.week_cadence > 1 && !rowCadence) {
              rowCadence = s.week_cadence;
            }
            if (Array.isArray(s.weeks_of_month) && s.weeks_of_month.length > 0 && s.weeks_of_month.length < 4 && !rowWom) {
              rowWom = `${s.weeks_of_month.length} of 4`;
            }
          }
        }
        return { mondayISO, row, rowCadence, rowWom };
      });
  }, [monthDates, mySessions]);

  return (
    <div className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm overflow-hidden" data-testid="schedule-month-view">
      <div className="grid grid-cols-[80px_repeat(5,1fr)] border-b border-[#ECEAE4] bg-[#F3F1EC]">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] py-2 pl-3" />
        {DAY_LABELS.map((d) => (
          <span
            key={d}
            className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] py-2 px-2"
          >
            {d}
          </span>
        ))}
      </div>
      <div className="divide-y divide-[#ECEAE4]">
        {grid.map(({ mondayISO, row, rowCadence, rowWom }, wIdx) => {
          const mondayDate = new Date(mondayISO + "T12:00:00");
          const weekLabel = mondayDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
          // Iter 33d Follow-up 14f — cadence-week tinting. When the row
          // contains an all-day cadence-bearing personal block, give the
          // left header cell a soft tint to make the cadence pattern
          // visually obvious across many weeks.
          const hasAnyAllDay = row.some((c) => c && c.allDay && c.allDay.length > 0);
          return (
            <div
              key={mondayISO}
              data-testid={`month-row-${mondayISO}`}
              data-row-cadence={rowCadence || ""}
              className="grid grid-cols-[80px_repeat(5,1fr)] min-h-[140px]"
            >
              <div className={`flex flex-col items-start justify-start pt-3 pl-3 border-r border-[#ECEAE4] ${hasAnyAllDay ? "bg-[#F3F1EC]" : "bg-[#FAFAF7]"}`}>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
                  Week {wIdx + 1}
                </span>
                <span className="text-[10px] text-[#9B9890]">{weekLabel}</span>
                {rowCadence && (
                  <Badge
                    data-testid={`month-row-cadence-${mondayISO}`}
                    className="mt-1 bg-[#1E3F33]/10 text-[#1E3F33] hover:bg-[#1E3F33]/15 text-[9px] px-1.5 py-0 rounded font-semibold"
                  >
                    every {rowCadence === 2 ? "2nd" : rowCadence === 3 ? "3rd" : `${rowCadence}th`} wk
                  </Badge>
                )}
                {rowWom && !rowCadence && (
                  <Badge
                    className="mt-1 bg-[#1E3F33]/10 text-[#1E3F33] hover:bg-[#1E3F33]/15 text-[9px] px-1.5 py-0 rounded font-semibold"
                  >
                    {rowWom}
                  </Badge>
                )}
              </div>
              {DAY_LABELS.map((_, dIdx) => {
                const cell = row[dIdx];
                return (
                  <div
                    key={dIdx}
                    data-testid={`month-cell-${mondayISO}-${dIdx}`}
                    className={`border-l border-[#ECEAE4] p-2 space-y-1.5 ${cell ? "" : "bg-[#FAFAF7]/40"}`}
                  >
                    {cell ? (
                      <>
                        <p className="text-[10px] text-[#9B9890] tabular-nums">
                          {new Date(cell.date + "T12:00:00").toLocaleDateString("en-US", {
                            month: "numeric",
                            day: "numeric",
                          })}
                        </p>
                        {/* Iter 33d Follow-up 14f — All-day personal
                            blocks render as a full-width banner so the
                            "Every day · All day" rule is visually
                            obvious on its cadence weeks. */}
                        {cell.allDay && cell.allDay.length > 0 && (
                          <div className="space-y-1">
                            {cell.allDay.map((s) => {
                              const c = providerBlockColor(s.personal_kind);
                              return (
                                <div
                                  key={s.id}
                                  data-testid={`month-allday-${s.id}`}
                                  className="rounded-md px-2 py-1 text-[10px] font-semibold flex items-center justify-between gap-1 ring-1 ring-inset"
                                  style={{
                                    background: c.bg,
                                    color: c.text,
                                    borderLeft: `3px solid ${c.border}`,
                                    boxShadow: `inset 0 0 0 1px ${c.border}33`,
                                  }}
                                >
                                  <span className="truncate">
                                    All day · {s.personal_label || s.personal_kind || "Personal"}
                                  </span>
                                  {typeof s.week_cadence === "number" && s.week_cadence > 1 && (
                                    <span className="text-[9px] tabular-nums opacity-80 shrink-0">
                                      every {s.week_cadence === 2 ? "2nd" : s.week_cadence === 3 ? "3rd" : `${s.week_cadence}th`} wk
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {cell.sessions.length === 0 && (!cell.allDay || cell.allDay.length === 0) ? (
                          <p className="text-[10px] text-[#9B9890] italic">—</p>
                        ) : (
                          cell.sessions.map((s) => (
                            <MonthChip
                              key={s.id}
                              session={s}
                              conflict={conflictSet.has(s.id)}
                              onClick={() => {
                                if (s.is_grouped && s.session_ids?.length > 0) {
                                  navigate(`/session?launch=${s.session_ids.join(",")}`);
                                } else if (s.students?.[0]) {
                                  navigate(`/students/${s.students[0].id}`);
                                }
                              }}
                            />
                          ))
                        )}
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="px-4 py-2 bg-[#FAFAF7] border-t border-[#ECEAE4] flex items-center gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Legend</span>
        <span className="text-[10px] text-[#6B7280] flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded-sm" style={{ background: "#E1EAE4", borderLeft: "2px solid #1E3F33" }} />
          recurring service
        </span>
        <span className="text-[10px] text-[#6B7280] flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded-sm" style={{ background: "#F3F1EC", borderLeft: "2px solid #9B9890" }} />
          personal block
        </span>
        <span className="text-[10px] text-[#6B7280] flex items-center gap-1">
          <span className="inline-block w-4 h-2.5 rounded-sm ring-1 ring-inset" style={{ background: "#F3F1EC", borderLeft: "2px solid #9B9890", boxShadow: "inset 0 0 0 1px #9B989033" }} />
          all-day banner
        </span>
        <span className="text-[10px] text-[#6B7280] flex items-center gap-1">
          <Badge className="bg-[#1E3F33]/10 text-[#1E3F33] hover:bg-[#1E3F33]/15 text-[9px] px-1.5 py-0 rounded font-semibold">every Nth wk</Badge>
          row cadence
        </span>
        <span className="text-[10px] text-[#6B7280]">·</span>
        <span className="text-[10px] text-[#6B7280] italic">empty cells = no session that week (school closed, off day, or cadence skip)</span>
      </div>
    </div>
  );
}

function MonthChip({ session, conflict, onClick }) {
  const isPersonal = session.block_kind === "personal";
  const color = isPersonal
    ? providerBlockColor(session.personal_kind)
    : gc(session.students);
  const isGrouped = !!session.is_grouped;
  // FP3-034E polish — Stronger Month-view treatment so OOO sessions
  // read at a glance even at 28px wide. The whole chip swaps to the
  // treatment palette (warm-amber for make-up, warm-grey for excused)
  // with a thicker 3px left accent, a diagonal out-of-service stripe,
  // and a SOLID-COLOR pill at the top so providers don't have to
  // scan for the tiny status text anymore.
  //
  // FP3-034F — Read the palette from the shared `TREATMENT_META`
  // export so the month-chip stays in lockstep with `UnavailableBadge`.
  // The chip uses the `*Hex` variants for inline `style` props.
  const unavail = session.provider_unavailable
    ? (() => {
        const treatment = session.unavail_fidelity_treatment || "make_up_needed";
        const palette = TREATMENT_META[treatment] || TREATMENT_META.make_up_needed;
        return {
          treatment,
          reason: session.unavail_reason || "other",
          chipBg: palette.chipBgHex,
          chipText: palette.chipTextHex,
          chipLabel: palette.shortLabel,
          bg: palette.cardBgHex,
          border: palette.cardBorderHex,
          accent: palette.cardAccentHex,
          text: palette.cardTextHex,
          stripe: palette.stripePattern,
        };
      })()
    : null;
  const wom = session.weeks_of_month;
  const cadence = Array.isArray(wom) && wom.length > 0 && wom.length < 4
    ? `${wom.length} of 4`
    : null;
  const label = isPersonal
    ? (session.personal_label || session.personal_kind || "Personal")
    : isGrouped
    ? `${session.students.slice(0, 2).map((s) => s.first_name).join(", ")}${
        session.students.length > 2 ? ` +${session.students.length - 2}` : ""
      }`
    : session.students?.[0]
    ? session.students[0].first_name
    : session.service_type || "—";
  return (
    <button
      data-testid={`month-chip-${session.id}`}
      data-provider-unavailable={unavail ? "true" : "false"}
      data-unavail-treatment={unavail?.treatment || ""}
      onClick={onClick}
      title={
        unavail
          ? `Provider ${unavail.reason} — ${
              unavail.treatment === "make_up_needed" ? "Needs make-up" : "Excused no-service"
            }`
          : undefined
      }
      className={`block w-full text-left px-1.5 py-1 rounded text-[10px] leading-tight transition-shadow hover:shadow-sm ${
        conflict ? "ring-1 ring-[#B85C5C]/50" : ""
      }`}
      style={{
        background: unavail ? unavail.bg : color.bg,
        backgroundImage: unavail ? unavail.stripe : undefined,
        borderLeft: `${unavail ? "3px" : "2px"} solid ${unavail ? unavail.accent : color.border}`,
        color: unavail ? unavail.text : color.text,
      }}
    >
      {/* FP3-034E polish — Solid-color status pill at the TOP of the
          chip. Replaces the tiny text in the corner so the OOO state
          is the first thing the eye lands on, even in the compressed
          month grid. */}
      {unavail && (
        <span
          data-testid={`month-unavail-${session.id}`}
          className="inline-block text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full mb-0.5 leading-none"
          style={{ backgroundColor: unavail.chipBg, color: unavail.chipText }}
        >
          {unavail.chipLabel}
        </span>
      )}
      <p
        className={`font-semibold truncate ${
          unavail ? "line-through decoration-[#9B9890]/60 decoration-1" : ""
        }`}
      >
        {label}
      </p>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[9px] tabular-nums opacity-75">{fmtTime(session.start_time)}</span>
        {!unavail && cadence ? (
          <Badge
            className="bg-[#1E3F33]/10 text-[#1E3F33] hover:bg-[#1E3F33]/15 text-[8px] px-1 py-0 rounded"
            data-testid={`month-cadence-${session.id}`}
          >
            {cadence}
          </Badge>
        ) : !unavail && isGrouped ? (
          <span className="text-[8px] font-bold tabular-nums opacity-75">·{session.students.length}</span>
        ) : null}
      </div>
    </button>
  );
}
